import { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { WhatsappLogo, Image, Clock, Users, PaperPlaneTilt, Trash, Eye, FloppyDisk, CalendarPlus, ArrowsClockwise, CheckCircle, XCircle } from "@phosphor-icons/react";
import QuickReportButton from "@/components/QuickReportButton";

const ROLES = ["jugador", "directivo", "profesor", "admin"];
const CATEGORIES = ["6", "5", "4", "3", "2", "1", "open", "A", "B", "C", "D"];

function ChipToggle({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1 text-xs font-semibold uppercase tracking-wider border transition-all ${
        active
          ? "bg-[#CCFF00] text-black border-[#CCFF00]"
          : "bg-transparent text-gray-400 border-white/15 hover:border-[#CCFF00]/50 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function AudienceEditor({ value, onChange, clubs }) {
  const v = value || { roles: ["jugador"], club_ids: null, categories: null, manual_phones: null };
  const toggleArr = (key, item) => {
    const cur = v[key] || [];
    const next = cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item];
    onChange({ ...v, [key]: next.length ? next : (key === "roles" ? ["jugador"] : null) });
  };
  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Roles destino</Label>
        <div className="flex flex-wrap gap-2" data-testid="audience-roles">
          {ROLES.map((r) => (
            <ChipToggle key={r} active={(v.roles || []).includes(r)} onClick={() => toggleArr("roles", r)} testId={`role-${r}`}>{r}</ChipToggle>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Categoría / Nivel (vacío = todas)</Label>
        <div className="flex flex-wrap gap-2" data-testid="audience-categories">
          {CATEGORIES.map((c) => (
            <ChipToggle key={c} active={(v.categories || []).includes(c)} onClick={() => toggleArr("categories", c)} testId={`cat-${c}`}>{c.toUpperCase()}</ChipToggle>
          ))}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Clubes (vacío = todos)</Label>
        <div className="flex flex-wrap gap-2 max-h-32 overflow-auto" data-testid="audience-clubs">
          {(clubs || []).map((c) => (
            <ChipToggle key={c.id} active={(v.club_ids || []).includes(c.id)} onClick={() => toggleArr("club_ids", c.id)} testId={`club-${c.id}`}>{c.name}</ChipToggle>
          ))}
          {(!clubs || clubs.length === 0) && <span className="text-xs text-gray-500">Sin clubes registrados</span>}
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Números manuales (uno por línea, E.164 con código país)</Label>
        <Textarea
          rows={3}
          value={(v.manual_phones || []).join("\n")}
          onChange={(e) => onChange({ ...v, manual_phones: e.target.value.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean) })}
          placeholder={"5215512345678\n5219991234567"}
          className="bg-black/50 border-white/10 font-mono text-sm"
          data-testid="audience-manual"
        />
      </div>
    </div>
  );
}

function AudiencePreviewButton({ audience }) {
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/whatsapp/audience/preview", audience);
      setCount(r.data?.count ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error preview");
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-3">
      <Button type="button" variant="outline" size="sm" onClick={run} disabled={busy} data-testid="btn-preview-audience">
        <Eye size={14} className="mr-1" /> Vista previa de audiencia
      </Button>
      {count !== null && <Badge className="bg-[#CCFF00]/20 text-[#CCFF00] border border-[#CCFF00]/40">{count} destinatarios</Badge>}
    </div>
  );
}

// ───── Config Tab ─────
function ConfigTab() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    phone_number_id: "", business_account_id: "", access_token: "", webhook_verify_token: "", default_country_code: "52", business_phone_number: "524641694920",
  });
  const [testTo, setTestTo] = useState("");
  const [testBody, setTestBody] = useState("Prueba desde Disfruta y Diviértete ✅");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/whatsapp/config");
      setCfg(r.data);
      setForm((p) => ({
        ...p,
        phone_number_id: r.data.phone_number_id || "",
        business_account_id: r.data.business_account_id || "",
        default_country_code: r.data.default_country_code || "52",
        business_phone_number: r.data.business_phone_number || "524641694920",
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cargar config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.put("/admin/whatsapp/config", form);
      toast.success("Configuración guardada");
      setForm((p) => ({ ...p, access_token: "", webhook_verify_token: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const sendTest = async () => {
    try {
      const r = await api.post("/admin/whatsapp/test", { to: testTo, body: testBody });
      if (r.data?.result?.error) {
        toast.error("Falla en Meta: " + JSON.stringify(r.data.result.response || r.data.result.message || ""));
      } else {
        toast.success(`Enviado a ${r.data.to}`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Error de envío"); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white/5 border border-white/10 p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Credenciales Meta WhatsApp Cloud API</h3>
          {cfg?.configured ? (
            <Badge className="bg-green-500/20 text-green-300 border border-green-500/40"><CheckCircle size={14} className="mr-1" /> Configurado</Badge>
          ) : (
            <Badge className="bg-yellow-500/20 text-yellow-300 border border-yellow-500/40"><XCircle size={14} className="mr-1" /> Sin configurar</Badge>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Phone Number ID</Label>
            <Input value={form.phone_number_id} onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
              placeholder="123456789012345" className="bg-black/50 border-white/10" data-testid="cfg-phone-id" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Business Account ID</Label>
            <Input value={form.business_account_id} onChange={(e) => setForm({ ...form, business_account_id: e.target.value })}
              placeholder="987654321098765" className="bg-black/50 border-white/10" data-testid="cfg-business-id" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-400">Access Token (Permanent)</Label>
            <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              placeholder={cfg?.access_token_masked || "EAA…"} className="bg-black/50 border-white/10 font-mono" data-testid="cfg-token" />
            <p className="text-xs text-gray-500 mt-1">Actual: <span className="font-mono">{cfg?.access_token_masked || "—"}</span></p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Webhook Verify Token</Label>
            <Input type="password" value={form.webhook_verify_token} onChange={(e) => setForm({ ...form, webhook_verify_token: e.target.value })}
              placeholder={cfg?.webhook_verify_token_masked || "string secreto"} className="bg-black/50 border-white/10 font-mono" data-testid="cfg-verify" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Código país por defecto</Label>
            <Input value={form.default_country_code} onChange={(e) => setForm({ ...form, default_country_code: e.target.value })}
              placeholder="52" className="bg-black/50 border-white/10" data-testid="cfg-cc" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Número remitente (display)</Label>
            <Input value={form.business_phone_number} onChange={(e) => setForm({ ...form, business_phone_number: e.target.value })}
              placeholder="524641694920" className="bg-black/50 border-white/10 font-mono" data-testid="cfg-business-phone" />
            <p className="text-[10px] text-gray-500 mt-1">Este número aparece como remitente en los mensajes. Asegúrate que esté registrado en Meta Business y vinculado al <b>Phone Number ID</b> de arriba.</p>
          </div>
        </div>
        {form.business_phone_number && (
          <div className="bg-green-500/10 border border-green-500/30 px-3 py-2 text-xs text-green-300 flex items-center gap-2">
            <WhatsappLogo size={14} weight="fill" /> Los mensajes se enviarán desde <b className="font-mono">+{form.business_phone_number}</b> · disfrutaydiviertete.com
          </div>
        )}
        <Button onClick={save} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="btn-save-config">
          <FloppyDisk size={16} className="mr-2" /> Guardar configuración
        </Button>
      </div>

      <div className="bg-white/5 border border-white/10 p-5 space-y-4">
        <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Enviar mensaje de prueba</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Número destino (E.164)</Label>
            <Input value={testTo} onChange={(e) => setTestTo(e.target.value)}
              placeholder="5215512345678" className="bg-black/50 border-white/10 font-mono" data-testid="test-to" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Cuerpo</Label>
            <Input value={testBody} onChange={(e) => setTestBody(e.target.value)} className="bg-black/50 border-white/10" data-testid="test-body" />
          </div>
        </div>
        <Button onClick={sendTest} disabled={!cfg?.configured} variant="outline" data-testid="btn-send-test">
          <PaperPlaneTilt size={16} className="mr-2" /> Enviar prueba
        </Button>
        <p className="text-xs text-gray-500">
          Nota: dentro de la ventana de 24h o a tu propio número de pruebas. Para mensajes proactivos a otros, usa plantillas aprobadas.
        </p>
      </div>

      <div className="bg-white/5 border border-white/10 p-5 space-y-3 text-sm text-gray-300">
        <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">¿Cómo obtener las credenciales?</h3>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Ve a <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-[#CCFF00] hover:underline">developers.facebook.com</a> → "My Apps" → Create App (tipo Business).</li>
          <li>En el dashboard: Add Product → <b>WhatsApp</b> → Set up. En "API Setup" copia <b>Phone Number ID</b> y <b>Business Account ID</b>.</li>
          <li>Para producción: <a href="https://business.facebook.com" target="_blank" rel="noreferrer" className="text-[#CCFF00] hover:underline">business.facebook.com</a> → Business Settings → System Users → crea uno tipo Admin → asigna WhatsApp Business Account → genera token permanente con scopes <code className="text-[#CCFF00]">whatsapp_business_messaging</code> + <code className="text-[#CCFF00]">whatsapp_business_management</code>.</li>
          <li><b>Webhook Verify Token:</b> inventa tú un string secreto (ej: <code>disfruta_padel_2026_xyz</code>). Lo configurarás en Meta cuando registres el webhook con URL: <code className="text-[#CCFF00]">{(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhook/whatsapp"}</code></li>
          <li>Crea plantillas en WhatsApp Manager → Message Templates (categoría Marketing) para mensajes proactivos fuera de ventana 24h.</li>
        </ol>
      </div>
    </div>
  );
}

// ───── Campaign Form ─────
function CampaignForm({ clubs, onCreated }) {
  const [name, setName] = useState("");
  const [messageType, setMessageType] = useState("text");
  const [textBody, setTextBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageCaption, setImageCaption] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [templateLanguage, setTemplateLanguage] = useState("es_MX");
  const [templateVars, setTemplateVars] = useState("");
  const [audience, setAudience] = useState({ roles: ["jugador"], club_ids: null, categories: null, manual_phones: null });
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/whatsapp/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(r.data.url || r.data.relative_url);
      toast.success("Imagen subida");
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir"); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Nombre de campaña requerido"); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: name.trim(),
        message_type: messageType,
        text_body: messageType === "text" ? textBody : (messageType === "image" ? imageCaption : null),
        image_url: messageType === "image" || messageType === "template" ? imageUrl : null,
        image_caption: imageCaption || null,
        template_name: messageType === "template" ? templateName : null,
        template_language: templateLanguage,
        template_variables: templateVars ? templateVars.split(",").map((s) => s.trim()).filter(Boolean) : null,
        audience,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      };
      const r = await api.post("/admin/whatsapp/campaigns", payload);
      toast.success(scheduledAt ? `Programada (${r.data.id.slice(0, 8)}…)` : `Enviando (${r.data.id.slice(0, 8)}…)`);
      setName(""); setTextBody(""); setImageCaption(""); setImageUrl(""); setTemplateName(""); setTemplateVars(""); setScheduledAt("");
      onCreated?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al crear campaña"); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="bg-white/5 border border-white/10 p-5 space-y-4" data-testid="campaign-form">
      <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white flex items-center gap-2">
        <CalendarPlus size={20} className="text-[#CCFF00]" /> Nueva Campaña
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Nombre</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Promo Torneo Verano" className="bg-black/50 border-white/10" data-testid="campaign-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Tipo de mensaje</Label>
          <Select value={messageType} onValueChange={setMessageType}>
            <SelectTrigger className="bg-black/50 border-white/10" data-testid="campaign-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="text">Texto</SelectItem>
              <SelectItem value="image">Imagen + caption</SelectItem>
              <SelectItem value="template">Plantilla aprobada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {messageType === "text" && (
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Texto (usa <code className="text-[#CCFF00]">{"{name}"}</code> para personalizar)</Label>
          <Textarea rows={4} value={textBody} onChange={(e) => setTextBody(e.target.value)} className="bg-black/50 border-white/10" data-testid="campaign-text" />
        </div>
      )}

      {(messageType === "image" || messageType === "template") && (
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Imagen — URL pública</Label>
            <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://… o /api/admin/whatsapp/media/xxx.jpg" className="bg-black/50 border-white/10 font-mono text-sm" data-testid="campaign-image-url" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">o sube archivo</Label>
            <input type="file" accept="image/*" onChange={(e) => onImageUpload(e.target.files?.[0])} disabled={uploading}
              className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#CCFF00] file:text-black file:font-semibold file:uppercase file:tracking-wider"
              data-testid="campaign-image-file" />
            {uploading && <p className="text-xs text-yellow-300 mt-1">Subiendo…</p>}
          </div>
          {messageType === "image" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-gray-400">Caption (opcional)</Label>
              <Textarea rows={2} value={imageCaption} onChange={(e) => setImageCaption(e.target.value)} className="bg-black/50 border-white/10" data-testid="campaign-caption" />
            </div>
          )}
        </div>
      )}

      {messageType === "template" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-gray-400">Nombre de plantilla</Label>
            <Input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="motivacion_diaria" className="bg-black/50 border-white/10 font-mono" data-testid="campaign-template-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400">Lang</Label>
            <Input value={templateLanguage} onChange={(e) => setTemplateLanguage(e.target.value)} placeholder="es_MX" className="bg-black/50 border-white/10 font-mono" data-testid="campaign-template-lang" />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs uppercase tracking-wider text-gray-400">Variables (separadas por coma, en orden)</Label>
            <Input value={templateVars} onChange={(e) => setTemplateVars(e.target.value)} placeholder="{name}, Torneo Verano, 15-Mar" className="bg-black/50 border-white/10" data-testid="campaign-template-vars" />
          </div>
        </div>
      )}

      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2 mb-2"><Users size={14} /> Audiencia</Label>
        <AudienceEditor value={audience} onChange={setAudience} clubs={clubs} />
        <div className="mt-3"><AudiencePreviewButton audience={audience} /></div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400">Programar envío (opcional, vacío = inmediato)</Label>
        <Input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="bg-black/50 border-white/10" data-testid="campaign-schedule" />
      </div>

      <Button onClick={submit} disabled={submitting} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80 w-full md:w-auto" data-testid="btn-create-campaign">
        <PaperPlaneTilt size={16} className="mr-2" /> {scheduledAt ? "Programar campaña" : "Enviar ahora"}
      </Button>
    </div>
  );
}

// ───── Campaigns list ─────
function CampaignsList({ items, onRefresh, onRunNow, onDelete }) {
  if (!items.length) return <p className="text-sm text-gray-500 italic">Sin campañas todavía.</p>;
  return (
    <div className="space-y-3" data-testid="campaigns-list">
      {items.map((c) => (
        <div key={c.id} className="bg-white/5 border border-white/10 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`campaign-row-${c.id}`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-white truncate">{c.name}</span>
              <Badge className="bg-white/10 text-gray-300 text-xs uppercase">{c.message_type}</Badge>
              {c.is_daily_motivation && <Badge className="bg-amber-500/20 text-amber-200 text-xs">Motivación</Badge>}
              <Badge className={
                c.status === "completed" ? "bg-green-500/20 text-green-300" :
                c.status === "running" ? "bg-blue-500/20 text-blue-300" :
                c.status === "failed" ? "bg-red-500/20 text-red-300" :
                c.status === "scheduled" ? "bg-yellow-500/20 text-yellow-300" :
                "bg-white/10 text-gray-300"
              }>{c.status}</Badge>
              {c.results_count > 0 && <Badge className="bg-white/10 text-xs">{c.results_count} enviados</Badge>}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Creada: {new Date(c.created_at).toLocaleString()}{c.scheduled_at ? ` · Programada: ${new Date(c.scheduled_at).toLocaleString()}` : ""}
              {c.sent_at ? ` · Enviada: ${new Date(c.sent_at).toLocaleString()}` : ""}
            </p>
          </div>
          <div className="flex gap-2">
            {(c.status === "scheduled" || c.status === "pending") && (
              <Button size="sm" variant="outline" onClick={() => onRunNow(c.id)} data-testid={`btn-run-${c.id}`}>
                <PaperPlaneTilt size={14} className="mr-1" /> Enviar ya
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onDelete(c.id)} className="text-red-400 hover:text-red-300" data-testid={`btn-delete-${c.id}`}>
              <Trash size={14} />
            </Button>
          </div>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={onRefresh} data-testid="btn-refresh-campaigns">
        <ArrowsClockwise size={14} className="mr-1" /> Refrescar
      </Button>
    </div>
  );
}

// ───── Daily Motivation tab ─────
function DailyMotivationTab({ clubs }) {
  const [form, setForm] = useState({
    enabled: false, hour: 8, minute: 0, messages: [], image_url: "", template_name: "",
    audience: { roles: ["jugador"], club_ids: null, categories: null, manual_phones: null },
  });
  const [messagesText, setMessagesText] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/whatsapp/daily-motivation");
      setForm({
        enabled: !!r.data.enabled,
        hour: r.data.hour ?? 8,
        minute: r.data.minute ?? 0,
        messages: r.data.messages || [],
        image_url: r.data.image_url || "",
        template_name: r.data.template_name || "",
        audience: r.data.audience || { roles: ["jugador"], club_ids: null, categories: null, manual_phones: null },
      });
      setMessagesText((r.data.messages || []).join("\n"));
    } catch (e) { toast.error("Error al cargar motivación"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onImageUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/whatsapp/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, image_url: r.data.url || r.data.relative_url }));
      toast.success("Imagen subida");
    } catch (e) { toast.error("Error al subir"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    const msgs = messagesText.split("\n").map((s) => s.trim()).filter(Boolean);
    try {
      await api.put("/admin/whatsapp/daily-motivation", { ...form, messages: msgs });
      toast.success(form.enabled ? `Motivación diaria activada (${String(form.hour).padStart(2, "0")}:${String(form.minute).padStart(2, "0")} UTC)` : "Motivación diaria desactivada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const runNow = async () => {
    try {
      await api.post("/admin/whatsapp/daily-motivation/run-now");
      toast.success("Enviando ahora a la comunidad");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="bg-white/5 border border-white/10 p-5 space-y-4" data-testid="daily-form">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white flex items-center gap-2">
          <Clock size={20} className="text-[#CCFF00]" /> Motivación Diaria
        </h3>
        <div className="flex items-center gap-3">
          <Label className="text-xs uppercase tracking-wider text-gray-400">Activar</Label>
          <Switch checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} data-testid="daily-enabled" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Hora (UTC)</Label>
          <Input type="number" min={0} max={23} value={form.hour} onChange={(e) => setForm({ ...form, hour: parseInt(e.target.value) || 0 })} className="bg-black/50 border-white/10" data-testid="daily-hour" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Minuto</Label>
          <Input type="number" min={0} max={59} value={form.minute} onChange={(e) => setForm({ ...form, minute: parseInt(e.target.value) || 0 })} className="bg-black/50 border-white/10" data-testid="daily-minute" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs uppercase tracking-wider text-gray-400">Plantilla aprobada (opcional)</Label>
          <Input value={form.template_name} onChange={(e) => setForm({ ...form, template_name: e.target.value })}
            placeholder="motivacion_diaria (vacío = texto libre, solo válido en ventana 24h)" className="bg-black/50 border-white/10 font-mono" data-testid="daily-template" />
        </div>
      </div>
      <p className="text-xs text-gray-500">
        Tip: <b>Hora UTC</b>. Para que llegue a las 8:00 AM en CDMX (UTC-6), pon hora <b>14:00</b>.
      </p>

      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400">Mensajes rotativos (uno por línea, se elige uno cada día)</Label>
        <Textarea rows={6} value={messagesText} onChange={(e) => setMessagesText(e.target.value)}
          placeholder={"¡Buenos días, campeón! Hoy es un gran día para mejorar tu derecha.\nLa cancha no espera. ¡Vamos por esos puntos!\n…"}
          className="bg-black/50 border-white/10" data-testid="daily-messages" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">Imagen URL (opcional)</Label>
          <Input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
            placeholder="https://… o /api/admin/whatsapp/media/xxx.jpg" className="bg-black/50 border-white/10 font-mono text-sm" data-testid="daily-image-url" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-gray-400">o sube archivo</Label>
          <input type="file" accept="image/*" onChange={(e) => onImageUpload(e.target.files?.[0])} disabled={uploading}
            className="block w-full text-sm text-gray-300 file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-[#CCFF00] file:text-black file:font-semibold file:uppercase file:tracking-wider"
            data-testid="daily-image-file" />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-gray-400 flex items-center gap-2 mb-2"><Users size={14} /> Audiencia destinataria</Label>
        <AudienceEditor value={form.audience} onChange={(a) => setForm({ ...form, audience: a })} clubs={clubs} />
        <div className="mt-3"><AudiencePreviewButton audience={form.audience} /></div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Button onClick={save} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="btn-save-daily">
          <FloppyDisk size={16} className="mr-2" /> Guardar
        </Button>
        <Button onClick={runNow} variant="outline" data-testid="btn-run-daily">
          <PaperPlaneTilt size={16} className="mr-2" /> Enviar ahora (prueba)
        </Button>
      </div>
    </div>
  );
}

// ───── DYD WhatsApp Coach tab ─────
function CoachTab() {
  const [cfg, setCfg] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [openSession, setOpenSession] = useState(null);
  const [sessionMsgs, setSessionMsgs] = useState([]);
  const [testWaId, setTestWaId] = useState("");
  const [testText, setTestText] = useState("");
  const [testReply, setTestReply] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadCfg = useCallback(async () => {
    try { const r = await api.get("/admin/whatsapp-coach/config"); setCfg(r.data); }
    catch (e) { toast.error("Error al cargar config del coach"); }
  }, []);
  const loadSessions = useCallback(async () => {
    try { const r = await api.get("/admin/whatsapp-coach/sessions?limit=100"); setSessions(r.data.sessions || []); }
    catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { loadCfg(); loadSessions(); }, [loadCfg, loadSessions]);

  const openMessages = async (waId) => {
    setOpenSession(waId);
    try { const r = await api.get(`/admin/whatsapp-coach/sessions/${waId}/messages?limit=200`); setSessionMsgs(r.data.messages || []); }
    catch (e) { toast.error("Error al cargar mensajes"); }
  };

  const save = async () => {
    try {
      const r = await api.put("/admin/whatsapp-coach/config", {
        enabled: !!cfg.enabled,
        welcome_message: cfg.welcome_message || "",
        allow_unregistered: !!cfg.allow_unregistered,
        prompt_override: cfg.prompt_override || null,
      });
      setCfg(r.data);
      toast.success("Configuración guardada");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const runTest = async () => {
    if (!testWaId || !testText) return toast.error("Completa wa_id y texto");
    setTesting(true); setTestReply(null);
    try {
      const r = await api.post("/admin/whatsapp-coach/test-reply", { wa_id: testWaId, text: testText });
      setTestReply(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setTesting(false); }
  };

  if (!cfg) return <div className="text-gray-500" data-testid="coach-loading">Cargando…</div>;

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="coach-panel">
      <div className="lg:col-span-2 space-y-4">
        <div className="bg-black/40 border border-white/10 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Configuración del Coach</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Activo</span>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="coach-enabled-switch" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Mensaje de bienvenida (primer turno)</Label>
            <Textarea rows={3} value={cfg.welcome_message || ""} onChange={(e) => setCfg({ ...cfg, welcome_message: e.target.value })} className="bg-black/50 border-white/10" data-testid="coach-welcome" />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!cfg.allow_unregistered} onCheckedChange={(v) => setCfg({ ...cfg, allow_unregistered: v })} data-testid="coach-allow-unreg" />
            <Label className="text-xs uppercase tracking-wider text-gray-400">Responder a números no registrados</Label>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">Instrucciones extra al sistema (opcional)</Label>
            <Textarea rows={3} value={cfg.prompt_override || ""} onChange={(e) => setCfg({ ...cfg, prompt_override: e.target.value })} placeholder="Ej. 'Mencionar siempre el torneo XYZ que comienza el sábado.'" className="bg-black/50 border-white/10" data-testid="coach-prompt-override" />
          </div>
          <Button onClick={save} className="bg-[#CCFF00] text-black hover:bg-[#CCFF00]/80" data-testid="coach-save">
            <FloppyDisk size={16} className="mr-2" /> Guardar
          </Button>
        </div>

        <div className="bg-black/40 border border-white/10 p-5 space-y-3">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Probar respuesta (sin enviar a WhatsApp)</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input placeholder="wa_id (E.164, ej. 5215512345678)" value={testWaId} onChange={(e) => setTestWaId(e.target.value)} className="bg-black/50 border-white/10 font-mono" data-testid="coach-test-waid" />
            <Input placeholder="Mensaje del usuario" value={testText} onChange={(e) => setTestText(e.target.value)} className="bg-black/50 border-white/10" data-testid="coach-test-text" />
          </div>
          <Button onClick={runTest} disabled={testing} className="bg-white/10 hover:bg-white/20 border border-white/15 text-white" data-testid="coach-test-btn">
            <PaperPlaneTilt size={16} className="mr-2" /> {testing ? "Generando…" : "Probar"}
          </Button>
          {testReply && (
            <div className="bg-green-500/10 border border-green-500/30 p-3 text-sm whitespace-pre-wrap text-green-100" data-testid="coach-test-reply">
              <div className="text-xs text-green-300 uppercase tracking-wider mb-1">Reply de DYD</div>
              {testReply.reply}
              <div className="text-[10px] text-gray-400 mt-2">match: {testReply.matched_user || "anónimo"} · session: {testReply.session_id}</div>
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-1 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Conversaciones</h3>
          <Button size="sm" variant="outline" onClick={loadSessions} data-testid="coach-refresh-sessions"><ArrowsClockwise size={14} /></Button>
        </div>
        <div className="space-y-2 max-h-[420px] overflow-auto pr-1" data-testid="coach-sessions-list">
          {sessions.length === 0 && <div className="text-xs text-gray-500">Sin conversaciones aún.</div>}
          {sessions.map((s) => (
            <button
              key={s.wa_id}
              type="button"
              onClick={() => openMessages(s.wa_id)}
              className={`w-full text-left p-3 border transition-all ${openSession === s.wa_id ? "border-[#CCFF00] bg-[#CCFF00]/10" : "border-white/10 bg-black/30 hover:border-white/30"}`}
              data-testid={`coach-session-${s.wa_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-white font-medium">{s.contact_name || `+${s.wa_id}`}</span>
                <Badge variant="outline" className="text-[10px]">{s.message_count || 0}</Badge>
              </div>
              <div className="text-[10px] text-gray-400 font-mono">+{s.wa_id}</div>
              <div className="text-[10px] text-gray-500">{s.last_activity_at?.slice(0, 16).replace("T", " ")}</div>
            </button>
          ))}
        </div>

        {openSession && (
          <div className="bg-black/40 border border-white/10 p-3 space-y-2 max-h-[420px] overflow-auto" data-testid="coach-conversation">
            <div className="text-xs uppercase tracking-wider text-gray-400 sticky top-0 bg-black/80 py-1">Historial · +{openSession}</div>
            {sessionMsgs.map((m) => (
              <div key={m.id} className={`p-2 text-xs ${m.from_user ? "bg-blue-500/10 border-l-2 border-blue-400" : "bg-[#CCFF00]/5 border-l-2 border-[#CCFF00]"}`}>
                <div className="text-[10px] uppercase tracking-wider mb-1 opacity-70">{m.from_user ? "Usuario" : "DYD"} · {m.created_at?.slice(11, 16)}</div>
                <div className="text-gray-100 whitespace-pre-wrap">{m.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ───── Main page ─────
export default function AdminPublicidad() {
  const { user } = useAuth();
  const [tab, setTab] = useState("campaigns");
  const [clubs, setClubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);

  const loadClubs = useCallback(async () => {
    try {
      const r = await api.get("/clubs");
      setClubs(r.data?.clubs || r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try {
      const r = await api.get("/admin/whatsapp/campaigns");
      setCampaigns(r.data.campaigns || []);
    } catch (e) { toast.error("Error al cargar campañas"); }
  }, []);

  useEffect(() => { loadClubs(); loadCampaigns(); }, [loadClubs, loadCampaigns]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const onRunNow = async (cid) => {
    try { await api.post(`/admin/whatsapp/campaigns/${cid}/run-now`); toast.success("Enviando…"); loadCampaigns(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const onDelete = async (cid) => {
    if (!window.confirm("¿Eliminar campaña?")) return;
    try { await api.delete(`/admin/whatsapp/campaigns/${cid}`); toast.success("Eliminada"); loadCampaigns(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-publicidad-page">
      <div className="flex items-center gap-3 flex-wrap">
        <WhatsappLogo size={36} weight="fill" className="text-green-400" />
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Publicidad WhatsApp</h1>
          <p className="text-sm text-gray-400">Campañas masivas y motivación diaria vía Meta WhatsApp Cloud API</p>
        </div>
        <QuickReportButton section="whatsapp" variant="compact" />
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white/5 border border-white/10">
          <TabsTrigger value="campaigns" data-testid="tab-campaigns"><PaperPlaneTilt size={14} className="mr-1" /> Campañas</TabsTrigger>
          <TabsTrigger value="daily" data-testid="tab-daily"><Clock size={14} className="mr-1" /> Motivación Diaria</TabsTrigger>
          <TabsTrigger value="coach" data-testid="tab-coach"><WhatsappLogo size={14} className="mr-1" /> DYD Coach</TabsTrigger>
          <TabsTrigger value="config" data-testid="tab-config"><WhatsappLogo size={14} className="mr-1" /> Configuración</TabsTrigger>
        </TabsList>

        <TabsContent value="campaigns" className="space-y-6 mt-4">
          <CampaignForm clubs={clubs} onCreated={loadCampaigns} />
          <div>
            <h3 className="text-lg font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white mb-3">Historial</h3>
            <CampaignsList items={campaigns} onRefresh={loadCampaigns} onRunNow={onRunNow} onDelete={onDelete} />
          </div>
        </TabsContent>

        <TabsContent value="daily" className="mt-4">
          <DailyMotivationTab clubs={clubs} />
        </TabsContent>

        <TabsContent value="coach" className="mt-4">
          <CoachTab />
        </TabsContent>

        <TabsContent value="config" className="mt-4">
          <ConfigTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
